import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArchiveRestore, ArrowRight, MessageSquare, RefreshCw } from '@digital-twins/design-system/icons'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Button } from '@/components/ui/button'
import {
  AUDIENCE_PORTRAIT_PLACEHOLDERS,
  AudiencePortraitCards,
} from '@/components/audiences/AudiencePortraitCards'
import {
  AUDIENCE_WIZARD_BUTTON_CLASS,
  AUDIENCE_WIZARD_BUTTON_VARIANT,
} from '@/lib/constants/audience-wizard'
import { cn } from '@/lib/utils'
import { populationsApi } from '@/lib/api'
import type { WelcomeScreenProps } from '@/types/components/audiences/welcome-screen'

const CARD_COUNT = 3
const MAX_IMAGES = 12
const PORTRAIT_CACHE_KEY = 'audience-welcome:portrait-urls'

function readCachedPortraitUrls(): string[] | null {
  try {
    const raw = sessionStorage.getItem(PORTRAIT_CACHE_KEY)
    if (!raw) return null
    const urls = JSON.parse(raw) as string[]
    return Array.isArray(urls) && urls.length >= CARD_COUNT ? urls : null
  } catch {
    return null
  }
}

function writeCachedPortraitUrls(urls: string[]) {
  try {
    sessionStorage.setItem(PORTRAIT_CACHE_KEY, JSON.stringify(urls))
  } catch {
    // Best-effort — quota exceeded or private browsing.
  }
}

export function WelcomeScreen({
  onGetStarted,
  className,
  showResumeBanner,
  latestSessionName,
  isResuming,
  onResumeImport,
  onDismissResume,
}: WelcomeScreenProps) {
  const { t } = useTranslation('app')
  
  const [imageUrls, setImageUrls] = useState<string[]>(() => readCachedPortraitUrls() ?? [])
  const [isPlaceholder, setIsPlaceholder] = useState(imageUrls.length === 0)

  const applyRealUrls = useCallback((urls: string[]) => {
    setImageUrls(urls)
    setIsPlaceholder(false)
    writeCachedPortraitUrls(urls)
  }, [])

  useEffect(() => {
    if (!isPlaceholder) return

    let cancelled = false
    populationsApi
      .getRandomPortraitUrls(MAX_IMAGES)
      .then((urls) => {
        if (!cancelled && urls.length >= CARD_COUNT) applyRealUrls(urls)
      })
      .catch(() => {
        /* best-effort — placeholders stay visible */
      })
    return () => {
      cancelled = true
    }
  }, [isPlaceholder, applyRealUrls])

  return (
    <div
      data-testid="welcome-screen"
      className={cn(
        'flex w-full flex-1 flex-col items-center justify-center gap-8 px-6 py-8 text-center',
        className,
      )}
    >
      {showResumeBanner ? (
        <div
          data-testid="welcome-import-session-resume-banner"
          className="flex flex-col sm:flex-row w-full max-w-3xl gap-3 sm:items-center sm:gap-2 rounded-2xl bg-card px-4 sm:px-6 py-4"
        >
          <div className="flex min-w-0 flex-1 items-start gap-2">
            <ArchiveRestore className="mt-0.5 h-4 w-4 shrink-0 text-cta" />
            <p className="min-w-0 flex-1 text-left text-sm leading-[1.5] text-foreground dark:text-white">
              {t('audienceCreate.restorePrompt')}{' '}
              <span className="text-muted-foreground dark:text-[#BDBDBD]">
                {latestSessionName ?? t('audienceCreate.importSession')}
              </span>
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
              size="sm"
              data-testid="welcome-import-session-resume-button"
              onClick={() => onResumeImport?.()}
              disabled={isResuming}
              className="rounded-md"
            >
              {isResuming ? t('audienceCreate.resuming') : t('audienceCreate.resume')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="welcome-import-session-resume-dismiss"
              onClick={() => onDismissResume?.()}
              className="text-sm font-medium text-cta hover:bg-transparent hover:text-cta/80"
            >
              {t('audienceCreate.dismiss')}
            </Button>
          </div>
        </div>
      ) : null}

      {/* Main content block — stable height regardless of resume banner */}
      <div className="flex flex-col items-center gap-6 w-full">
        <AudiencePortraitCards
          imageUrls={imageUrls.length >= CARD_COUNT ? imageUrls : AUDIENCE_PORTRAIT_PLACEHOLDERS}
          isPlaceholder={isPlaceholder}
        />

        <div className="flex max-w-3xl flex-col gap-3">
          <Heading
            level={1}
            size="hero"
            className="font-display text-balance leading-[1.1] md:text-4xl"
          >
            {t('audienceCreate.heroTitle')}
          </Heading>
          <p className="mx-auto max-w-2xl text-balance font-sans text-[18px] font-normal leading-relaxed text-muted-foreground">
            {t('audienceCreate.heroSubtitle')}
          </p>
        </div>

        <div className="grid w-full max-w-3xl grid-cols-1 gap-4 text-center sm:grid-cols-2">
          <div className="flex flex-col items-center gap-3 rounded-[12px] bg-card/50 dark:bg-card px-5 py-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-card-inset">
              <MessageSquare className="h-5 w-5 text-primary" />
            </div>
            <div className="flex flex-col items-center gap-1">
              <Text size="base" weight="semibold" className="text-base leading-snug">
                {t('audienceCreate.askAnything')}
              </Text>
              <Text size="sm" color="muted" className="max-w-sm leading-relaxed">
                {t('audienceCreate.askAnythingBody')}
              </Text>
            </div>
          </div>
          <div className="flex flex-col items-center gap-3 rounded-[12px] bg-card/50 dark:bg-card px-5 py-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-card-inset">
              <RefreshCw className="h-5 w-5 text-primary" />
            </div>
            <div className="flex flex-col items-center gap-1">
              <Text size="base" weight="semibold" className="text-base leading-snug">
                {t('audienceCreate.enrich')}
              </Text>
              <Text size="sm" color="muted" className="max-w-sm leading-relaxed">
                {t('audienceCreate.enrichBody')}
              </Text>
            </div>
          </div>
        </div>

        <Button
          type="button"
          variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
          size="lg"
          className={AUDIENCE_WIZARD_BUTTON_CLASS}
          onClick={onGetStarted}
        >
          {t('audienceCreate.createCta')}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

    </div>
  )
}
